import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Download, FileSpreadsheet, Upload, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const StoreBulkUpload = ({ apiBase, filename, testidPrefix, onUploaded }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    const res = await axios.get(`${API}/api/${apiBase}/template`, { withCredentials: true, responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/api/${apiBase}/upload-entries`, fd, { withCredentials: true });
      setResult({ ok: true, data: res.data });
      onUploaded && onUploaded();
    } catch (e) {
      const d = e.response?.data;
      setResult({ ok: false, message: d?.message || d?.detail || e.message, errors: d?.errors || [] });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="bg-dash-panel border border-dash-line rounded-xl p-5 space-y-3" data-testid={`${testidPrefix}-bulk-upload`}>
      <div className="flex items-center gap-2">
        <FileSpreadsheet className="w-4 h-4 text-dash-ice" />
        <h3 className="text-sm font-semibold text-dash-ink">Bulk upload (Excel)</h3>
      </div>
      <p className="text-xs text-dash-mut">Download the template, fill one row per receipt or issue (Item Code from the Items sheet), then upload it. New items go on the template's New Items sheet (code is assigned automatically). If any row has an error, nothing is saved and every bad row is listed.</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={downloadTemplate}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-dash-line text-dash-ink hover:bg-dash-panel2 transition-colors"
          data-testid={`${testidPrefix}-download-template-btn`}>
          <Download className="w-3.5 h-3.5" /> Download template
        </button>
        <button onClick={() => fileRef.current?.click()} disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-dash-ice text-white disabled:opacity-40 transition-opacity"
          data-testid={`${testidPrefix}-upload-entries-btn`}>
          <Upload className="w-3.5 h-3.5" /> {busy ? 'Uploading…' : 'Upload filled template'}
        </button>
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden"
          onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testidPrefix}-upload-entries-input`} />
      </div>
      {result?.ok && (
        <div className="rounded-lg px-3 py-2 text-xs border border-dash-green/30 bg-[rgba(107,203,119,.08)] text-dash-ink flex items-start gap-1.5" data-testid={`${testidPrefix}-upload-success`}>
          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Saved {result.data.saved} entries ({result.data.receipts} receipts, {result.data.issues} issues) from {result.data.from_date}.
          {result.data.recomputed_dates?.length ? ` History recomputed through ${result.data.recomputed_dates[result.data.recomputed_dates.length - 1]}.` : ''}
          {result.data.new_items?.length ? ` New items created: ${result.data.new_items.map((n) => `${n.code} ${n.name}`).join(', ')}.` : ''}
        </div>
      )}
      {result && !result.ok && (
        <div className="rounded-lg px-3 py-2 text-xs border border-dash-coral/30 bg-[rgba(255,107,138,.08)] text-dash-coral space-y-1" data-testid={`${testidPrefix}-upload-errors`}>
          <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="w-3.5 h-3.5" /> {result.message}</div>
          {result.errors.length > 0 && (
            <ul className="max-h-48 overflow-auto space-y-0.5 pl-5 list-disc">
              {result.errors.map((e) => <li key={`${e.sheet}-${e.row}`}>{e.sheet || 'Entries'}{e.row ? ` row ${e.row}` : ''}: {e.errors.join('; ')}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
